import React, { useState, useEffect } from "react";
import { ChevronLeft, ChevronRight, X } from "lucide-react";

const ProjectLayout = ({
  title,
  location,
  year,
  description,
  gallery,
  additionalGallery,
  video,
  architects,
}) => {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);

  const gridImages =
    additionalGallery && additionalGallery.length > 0
      ? additionalGallery.map((src) => ({ src }))
      : [];

  const openLightbox = (index) => {
    setActiveIndex(index);
    setLightboxOpen(true);
    document.body.style.overflow = "hidden";
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
    document.body.style.overflow = "auto";
  };

  const nextImage = () =>
    setActiveIndex((prev) => (prev + 1) % gridImages.length);

  const prevImage = () =>
    setActiveIndex((prev) =>
      prev === 0 ? gridImages.length - 1 : prev - 1
    );

  useEffect(() => {
    const handleKey = (e) => {
      if (!lightboxOpen) return;
      if (e.key === "Escape") closeLightbox();
      if (e.key === "ArrowRight") nextImage();
      if (e.key === "ArrowLeft") prevImage();
    };

    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [lightboxOpen, gridImages.length]);

  return (
    <div className="relative w-full min-h-screen text-gray-900 overflow-x-hidden">
      {/* HERO BACKGROUND */}
      {gallery && gallery.length > 0 && (
        <div
          className="fixed inset-0 bg-cover bg-center bg-no-repeat z-0"
          style={{
            backgroundImage: `url(${gallery[0].src})`,
            filter: "brightness(0.65)",
          }}
        />
      )}

      <div className="absolute inset-0 bg-black/40 z-10" />

      {/* HERO CONTENT */}
      <div className="relative z-20 flex flex-col justify-end min-h-[85vh] px-6 md:px-24 pb-16 md:pb-24">
        <h1 className="text-white text-4xl sm:text-5xl md:text-8xl font-light mb-3 leading-tight">
          {title}
        </h1>
        <p className="text-gray-300 text-sm sm:text-base italic">
          {location} · {year}
        </p>
      </div>

      {/* MAIN CONTENT */}
      <div className="relative z-30 bg-white text-gray-800 -mt-16 md:-mt-24 pt-16 md:pt-24 pb-20 md:pb-32 shadow-2xl overflow-hidden">

        {/* DESCRIPTION */}
        {description && (
          <p className="max-w-4xl mx-auto text-base md:text-lg leading-relaxed px-6 md:px-0 mb-16 md:mb-20 text-gray-700">
            {description}
          </p>
        )}

        {/* NARRATIVE SECTION */}
        <div className="flex flex-col space-y-16 md:space-y-0">
          {gallery &&
            gallery.slice(1).map((item, index) => {
              const isLeft = index % 2 === 0;

              return (
                <div
                  key={index}
                  className={`flex flex-col md:flex-row items-center gap-8 md:gap-0 ${
                    isLeft ? "" : "md:flex-row-reverse"
                  }`}
                >
                  <div
                    className={`w-full md:w-1/2 ${
                      isLeft ? "md:pr-8" : "md:pl-8"
                    }`}
                  >
                    <img
                      src={item.src}
                      alt={item.viewTitle}
                      className="w-full h-[300px] md:h-full object-cover"
                      onContextMenu={(e) => e.preventDefault()}
                    />
                  </div>

                  <div className="w-full md:w-1/2 px-6 md:px-16">
                    <h3 className="text-xl md:text-2xl font-medium mb-2">
                      {item.viewTitle}
                    </h3>
                    <p className="text-gray-600 text-sm md:text-base">
                      {item.caption}
                    </p>
                  </div>
                </div>
              );
            })}
        </div>

        {/* GRID GALLERY */}
        {gridImages.length > 0 && (
          <section className="mt-24 md:mt-40 px-6 md:px-24">
            <h2 className="text-2xl md:text-3xl font-light mb-8 md:mb-10 text-center">
              Project Gallery
            </h2>

            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3 md:gap-4">
              {gridImages.map((img, i) => (
                <img
                  key={i}
                  src={img.src}
                  alt={`Gallery image ${i + 1}`}
                  onClick={() => openLightbox(i)}
                  onContextMenu={(e) => e.preventDefault()}
                  className="w-full h-32 sm:h-40 md:h-48 object-cover cursor-pointer hover:opacity-80 transition"
                  loading="lazy"
                />
              ))}
            </div>
          </section>
        )}

        {/* VIDEO SECTION */}
        {video && (
          <section className="mt-24 md:mt-40 text-center px-6 md:px-24">
            <h2 className="text-2xl md:text-3xl font-light mb-6">
              {video.title || "Project Walkthrough"}
            </h2>

            <div className="aspect-video w-full rounded-2xl overflow-hidden">
              {video.type === "local" && (
                <video
                  src={video.src}
                  poster={video.thumbnail}
                  controls
                  className="w-full h-full object-cover"
                />
              )}

              {video.type === "youtube" && (
                <iframe
                  src={`https://www.youtube.com/embed/${video.src}`}
                  className="w-full h-full"
                  allowFullScreen
                />
              )}
            </div>
          </section>
        )}

        {/* FOOTER */}
        {architects && (
          <footer className="text-center text-gray-500 mt-20 md:mt-32 text-sm md:text-base px-6">
            Designed by {architects.join(" & ")}
          </footer>
        )}
      </div>

      {/* LIGHTBOX */}
      {lightboxOpen && gridImages.length > 0 && (
        <div
          className="fixed inset-0 z-[999] bg-black/95 flex items-center justify-center"
          onClick={closeLightbox}
        >
          <button
            className="absolute top-4 right-4 md:top-6 md:right-6 text-white"
            onClick={closeLightbox}
          >
            <X size={28} />
          </button>

          <button
            className="absolute left-2 md:left-6 text-white"
            onClick={(e) => {
              e.stopPropagation();
              prevImage();
            }}
          >
            <ChevronLeft size={40} />
          </button>

          <img
            src={gridImages[activeIndex].src}
            className="max-h-[85vh] max-w-[95vw] object-contain"
            onClick={(e) => e.stopPropagation()}
            onContextMenu={(e) => e.preventDefault()}
          />

          <button
            className="absolute right-2 md:right-6 text-white"
            onClick={(e) => {
              e.stopPropagation();
              nextImage();
            }}
          >
            <ChevronRight size={40} />
          </button>
        </div>
      )}
    </div>
  );
};

export default ProjectLayout;
